// Updated Dashboard.js
import {
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  CheckboxGroup,
  Flex,
  Heading,
  Input,
  Select,
  Skeleton,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Text,
} from "@chakra-ui/react";
import { Chart, registerables } from "chart.js";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { Bar, Line, Pie } from "react-chartjs-2";
import { Helmet } from "react-helmet-async";
import {
  FiBarChart2,
  FiDollarSign,
  FiFlag,
  FiHeart,
  FiPercent,
  FiPieChart,
  FiPlusCircle,
  FiRefreshCw,
  FiShare2,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { secureFetch } from "../data/secureFetch";
import { formatCentsToDollars } from "../store/calc-helpers";
import UniversalTable from "./UniversalTable";

Chart.register(...registerables);

const asArray = (value) => (Array.isArray(value) ? value : []);

const HelmetInfo = () => {
  const campaignImage = `https://${window.location.host}/images/share-image.png`;

  return (
    <Helmet>
      <title>Dashboard - PurelyGive</title>
      <meta
        name="description"
        content="Access your PurelyGive dashboard to manage campaigns, track donations, and monitor your fundraising progress all in one place."
      />
      <meta
        name="keywords"
        content="dashboard, fundraising overview, campaign management, donation tracking, PurelyGive"
      />
      <meta
        property="og:title"
        content="Your Fundraising Dashboard - PurelyGive"
      />
      <meta
        property="og:description"
        content="Manage your campaigns and monitor donations seamlessly with PurelyGive's intuitive dashboard."
      />
      <meta property="og:image" content={campaignImage} />
      <meta property="og:url" content="https://purelygive.com/dashboard" />
    </Helmet>
  );
};
const DashboardSkeleton = () => {
  return (
    <Box p={5}>
      <HelmetInfo />
      {/* HEADER SECTION */}
      <Skeleton mb={6}>
        <Flex direction="column" gap={2}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
            <Skeleton height="40px" width="200px" />
            <Flex align="center" gap={2} wrap="wrap">
              <Skeleton height="40px" width="200px" />
              <Skeleton height="40px" width="120px" />
            </Flex>
          </Flex>
          <Flex justify="flex-end">
            <Skeleton height="20px" width="200px" />
          </Flex>
        </Flex>
      </Skeleton>

      {/* CONTENT SECTION */}
      <Flex direction="column" gap={6}>
        {/* SUMMARY CARDS */}
        <Flex direction={{ base: "column", md: "row" }} gap={6} wrap="wrap">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} flex="1" minW={{ base: "100%", md: "200px" }}>
              <Box p={6} borderRadius="md">
                <Flex direction="column" gap={2}>
                  <Skeleton height="20px" width="150px" mb={2} />
                  <Skeleton height="30px" width="100px" />
                </Flex>
              </Box>
            </Skeleton>
          ))}
        </Flex>

        {/* SHARE HINT */}
        <Skeleton>
          <Box p={4} borderRadius="md">
            <Flex direction={{ base: "column", md: "row" }} gap={3}>
              <Skeleton height="20px" width="300px" />
              <Flex gap={2}>
                <Skeleton height="40px" width="150px" />
                <Skeleton height="40px" width="120px" />
              </Flex>
            </Flex>
          </Box>
        </Skeleton>

        {/* FILTERS */}
        <Flex direction={{ base: "column", md: "row" }} gap={6}>
          <Box flex="1">
            <Skeleton height="20px" width="150px" mb={3} />
            <Stack spacing={2}>
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} height="20px" width="200px" />
              ))}
            </Stack>
          </Box>
          <Box flex="1">
            <Skeleton height="20px" width="150px" mb={3} />
            <Stack spacing={2}>
              <Skeleton height="20px" width="100px" />
              <Skeleton height="20px" width="100px" />
            </Stack>
          </Box>
        </Flex>

        {/* CHARTS SECTION 1 */}
        <Flex direction={{ base: "column", md: "row" }} gap={6} wrap="wrap">
          <Skeleton flex="1" minW={{ base: "100%", md: "300px" }}>
            <Box p={4} borderRadius="md">
              <Skeleton height="25px" width="200px" mb={4} />
              <Skeleton height="250px" width="100%" />
            </Box>
          </Skeleton>
          <Skeleton flex="1" minW={{ base: "100%", md: "300px" }}>
            <Box p={4} borderRadius="md">
              <Skeleton height="25px" width="200px" mb={4} />
              <Skeleton height="250px" width="100%" />
            </Box>
          </Skeleton>
        </Flex>

        {/* CHARTS SECTION 2 */}
        <Flex direction={{ base: "column", md: "row" }} gap={6} wrap="wrap">
          <Skeleton flex="1" minW={{ base: "100%", md: "300px" }}>
            <Box p={4} borderRadius="md">
              <Skeleton height="25px" width="200px" mb={4} />
              <Skeleton height="250px" width="100%" />
            </Box>
          </Skeleton>
          <Skeleton flex="1" minW={{ base: "100%", md: "300px" }}>
            <Box p={4} borderRadius="md">
              <Skeleton height="25px" width="250px" mb={4} />
              <Skeleton height="250px" width="100%" />
            </Box>
          </Skeleton>
        </Flex>

        {/* RECENT ACTIVITY */}
        <Flex direction={{ base: "column", md: "row" }} gap={6} wrap="wrap">
          <Skeleton flex="1" minW={{ base: "100%", md: "300px" }}>
            <Box p={4} borderRadius="md">
              <Skeleton height="25px" width="180px" mb={4} />
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} height="40px" width="100%" mb={2} />
              ))}
            </Box>
          </Skeleton>
          <Skeleton flex="1" minW={{ base: "100%", md: "300px" }}>
            <Box p={4} borderRadius="md">
              <Skeleton height="25px" width="200px" mb={4} />
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} height="40px" width="100%" mb={2} />
              ))}
            </Box>
          </Skeleton>
        </Flex>
      </Flex>
    </Box>
  );
};

function Dashboard() {
  const { organizationId } = useSelector((state) => state.user);

  // Summary stats
  const [totalFundsRaised, setTotalFundsRaised] = useState(0);
  const [totalActiveCampaigns, setTotalActiveCampaigns] = useState(0);
  const [totalDonations, setTotalDonations] = useState(0);
  const [totalDonors, setTotalDonors] = useState(0);
  const [averageDonationAmount, setAverageDonationAmount] = useState(0);
  const [totalFeesSaved, setTotalFeesSaved] = useState(0);

  // Chart data
  const [donationsOverTimeData, setDonationsOverTimeData] = useState({});
  const [donationsByCampaignData, setDonationsByCampaignData] = useState({});
  const [donationsByTypeData, setDonationsByTypeData] = useState({});
  const [donationAmountDistributionData, setDonationAmountDistributionData] =
    useState({});

  // Recent activity
  const [recentDonations, setRecentDonations] = useState([]);
  const [recentSubscriptions, setRecentSubscriptions] = useState([]);

  // Filters
  const [timeFrame, setTimeFrame] = useState("Last 30 Days");
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [donationType, setDonationType] = useState(["One-time", "Monthly"]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Date range for the selected time frame
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(new Date());

  // Fetch campaigns on initial load
  useEffect(() => {
    if (!organizationId) {
      setCampaigns([]);
      setSelectedCampaigns([]);
      return;
    }

    const fetchCampaigns = async () => {
      try {
        const campaignsResponse = await secureFetch(
          `/api/campaigns/${organizationId}?itemsPerPage=1000`
        );
        const campaignsData = asArray(campaignsResponse?.data ?? campaignsResponse);
        setCampaigns(campaignsData);
        setSelectedCampaigns(campaignsData.map((c) => c.id.toString()));
      } catch (err) {
        console.error("Error fetching campaigns:", err);
        setCampaigns([]);
        setSelectedCampaigns([]);
      }
    };
    fetchCampaigns();
  }, [organizationId]);

  // Fetch summary stats and recent activity
  const fetchData = useCallback(async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate.toISOString());
      if (endDate) params.append("endDate", endDate.toISOString());

      // Summary stats
      const statsResponse = await secureFetch(
        `/api/donations/summary-stats/${organizationId}?${params.toString()}`
      );
      const statsData = statsResponse;

      setTotalFundsRaised(statsData.totalFundsRaised || 0);
      setTotalActiveCampaigns(statsData.totalActiveCampaigns || 0);
      setTotalDonations(statsData.totalDonations || 0);
      setTotalDonors(statsData.totalDonors || 0);
      setAverageDonationAmount(statsData.averageDonationAmount || 0);
      setTotalFeesSaved(statsData.totalFeesSaved || 0);

      // Recent activity
      const recentDonationsResponse = await secureFetch(
        `/api/donations/recent/${organizationId}`
      );
      const recentDonationsData = recentDonationsResponse;
      setRecentDonations(asArray(recentDonationsData));

      const recentSubscriptionsResponse = await secureFetch(
        `/api/subscriptions/recent/${organizationId}`
      );
      const recentSubscriptionsData = recentSubscriptionsResponse;
      setRecentSubscriptions(asArray(recentSubscriptionsData));

      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [organizationId, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchChartData = useCallback(async () => {
    if (!organizationId) {
      return;
    }

    try {
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate.toISOString());
      if (endDate) params.append("endDate", endDate.toISOString());
      if (selectedCampaigns.length > 0)
        params.append("campaigns", selectedCampaigns.join(","));
      if (donationType.length > 0)
        params.append("donationType", donationType.join(","));

      const [
        donationsOverTimeDataRaw,
        donationsByCampaignDataRaw,
        donationsByTypeDataRaw,
        donationAmountDistributionDataRaw,
      ] = await Promise.all([
        secureFetch(
          `/api/donations/over-time/${organizationId}?${params.toString()}`
        ),
        secureFetch(
          `/api/donations/by-campaign/${organizationId}?${params.toString()}`
        ),
        secureFetch(
          `/api/donations/by-type/${organizationId}?${params.toString()}`
        ),
        secureFetch(
          `/api/donations/amount-distribution/${organizationId}?${params.toString()}`
        ),
      ]);

      setDonationsOverTimeData(
        formatDonationsOverTimeData(asArray(donationsOverTimeDataRaw))
      );
      setDonationsByCampaignData(
        formatDonationsByCampaignData(asArray(donationsByCampaignDataRaw))
      );
      setDonationsByTypeData(formatDonationsByTypeData(donationsByTypeDataRaw));
      setDonationAmountDistributionData(
        formatDonationAmountDistributionData(
          asArray(donationAmountDistributionDataRaw)
        )
      );
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Error fetching chart data:", err);
    }
  }, [donationType, endDate, organizationId, selectedCampaigns, startDate]);

  // Fetch chart data when filters change
  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  const handleRefresh = () => {
    fetchData();
    fetchChartData();
  };

  // Helpers to determine if charts have any real data
  const hasChartDatasetValues = (chartData) => {
    if (!chartData || !Array.isArray(chartData.datasets)) return false;
    return chartData.datasets.some(
      (ds) => Array.isArray(ds.data) && ds.data.some((v) => Number(v) > 0)
    );
  };

  const hasLabels = (chartData) =>
    Array.isArray(chartData?.labels) && chartData.labels.length > 0;

  const formatDonationsOverTimeData = (data) => ({
    labels: data.map((item) => new Date(item.date).toLocaleDateString()),
    datasets: [
      {
        label: "Donations",
        data: data.map((item) =>
          formatCentsToDollars(parseInt(item.total_amount))
        ),
        fill: false,
        borderColor: "#36A2EB",
      },
    ],
  });

  const formatDonationsByCampaignData = (data) => ({
    labels: data.map((item) => item.campaign_name),
    datasets: [
      {
        label: "Donations by Campaign",
        data: data.map((item) =>
          formatCentsToDollars(parseInt(item.total_amount))
        ),
        backgroundColor: "#FF6384",
      },
    ],
  });

  const formatDonationsByTypeData = (data) => ({
    labels: ["One-time", "Monthly"],
    datasets: [
      {
        data: [data.one_time || 0, data.monthly || 0],
        backgroundColor: ["#FF6384", "#36A2EB"],
      },
    ],
  });

  const formatDonationAmountDistributionData = (data) => ({
    labels: data.map(
      (item) =>
        `$${formatCentsToDollars(
          parseInt(item.range_start)
        )} - $${formatCentsToDollars(parseInt(item.range_end))}`
    ),
    datasets: [
      {
        label: "Number of Donations",
        data: data.map((item) => item.count),
        backgroundColor: "#FFCE56",
      },
    ],
  });

  // Enhanced time frame handler using new Date() copies to avoid mutating the current date.
  const handleTimeFrameChange = (e) => {
    const selectedTimeFrame = e.target.value;
    setTimeFrame(selectedTimeFrame);

    const now = new Date();
    let start = null;
    let end = null;

    switch (selectedTimeFrame) {
      case "Today":
        start = new Date(now);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      case "Last 3 Days":
        start = new Date(now);
        start.setDate(now.getDate() - 2);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      case "Last 7 Days":
        start = new Date(now);
        start.setDate(now.getDate() - 6);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      case "Last 30 Days":
        start = new Date(now);
        start.setDate(now.getDate() - 29);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      case "Last 12 Months":
        start = new Date(now);
        start.setFullYear(now.getFullYear() - 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      default:
        // Custom Range or All Time: leave start as null
        start = null;
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
    }

    setStartDate(start);
    setEndDate(end);
  };

  if (loading) {
    return <DashboardSkeleton />;
    // return (
    //   <Center>
    //     <Spinner />
    //   </Center>
    // );
  }

  if (error) {
    return <Box>Error: {error}</Box>;
  }

  const campaignImage = `https://${window.location.host}/images/share-image.png`;

  // Global empty state when there are no campaigns yet
  if (campaigns.length === 0) {
    return (
      <Box p={10} textAlign="center">
        <Helmet>
          <title>Dashboard - PurelyGive</title>
          <meta
            name="description"
            content="Start your first campaign to begin raising funds on PurelyGive."
          />
          <meta
            property="og:title"
            content="Your Fundraising Dashboard - PurelyGive"
          />
          <meta property="og:image" content={campaignImage} />
        </Helmet>
        <Center>
          <Flex direction="column" align="center" gap={4} maxW="640px">
            <Heading size="lg">Welcome to your Dashboard</Heading>
            <Text color="gray.600">
              You don’t have any campaigns yet. Create your first campaign to
              start raising funds and see live stats here.
            </Text>
            <Button
              as={Link}
              to="/campaigns/create"
              colorScheme="orange"
              leftIcon={<FiPlusCircle />}
            >
              Create New Campaign
            </Button>
          </Flex>
        </Center>
      </Box>
    );
  }

  return (
    <Box p={5}>
      <Helmet>
        <title>Dashboard - PurelyGive</title>
        <meta
          name="description"
          content="Access your PurelyGive dashboard to manage campaigns, track donations, and monitor your fundraising progress all in one place."
        />
        <meta
          name="keywords"
          content="dashboard, fundraising overview, campaign management, donation tracking, PurelyGive"
        />
        <meta
          property="og:title"
          content="Your Fundraising Dashboard - PurelyGive"
        />
        <meta
          property="og:description"
          content="Manage your campaigns and monitor donations seamlessly with PurelyGive's intuitive dashboard."
        />
        <meta property="og:image" content={campaignImage} />
        <meta property="og:url" content="https://purelygive.com/dashboard" />
      </Helmet>
      <Flex direction="column" gap={2} mb={6}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
          <Heading>Dashboard</Heading>
          <Flex align="center" gap={2} wrap="wrap">
            <Select
              value={timeFrame}
              onChange={handleTimeFrameChange}
              maxW="200px"
            >
              <option value="Today">Today</option>
              <option value="Last 3 Days">Last 3 Days</option>
              <option value="Last 7 Days">Last 7 Days</option>
              <option value="Last 30 Days">Last 30 Days</option>
              <option value="Last 12 Months">Last 12 Months</option>
              <option value="Custom Range">Custom Range</option>
            </Select>
            {timeFrame === "Custom Range" && (
              <Flex gap={2}>
                <Input
                  type="date"
                  onChange={(e) => setStartDate(new Date(e.target.value))}
                />
                <Input
                  type="date"
                  onChange={(e) => setEndDate(new Date(e.target.value))}
                />
              </Flex>
            )}
            <Button
              size="sm"
              leftIcon={<FiRefreshCw />}
              onClick={handleRefresh}
            >
              Refresh
            </Button>
          </Flex>
        </Flex>
        <Flex justify="flex-end">
          <Text fontSize="sm" color="gray.600">
            {lastUpdated
              ? `Last updated: ${lastUpdated.toLocaleString()}`
              : "Last updated: —"}
          </Text>
        </Flex>
      </Flex>
      <Flex direction="column" gap={6}>
        {/* Summary Cards */}
        <Flex direction={{ base: "column", md: "row" }} gap={6} wrap="wrap">
          <StatCard
            label="Total Funds Raised"
            value={`$${formatCentsToDollars(totalFundsRaised)}`}
            icon={<FiDollarSign />}
            iconColor="green.500"
          />
          <StatCard
            label="Total Active Campaigns"
            value={totalActiveCampaigns}
            icon={<FiFlag />}
            iconColor="blue.500"
          />
          <StatCard
            label="Total Donations"
            value={totalDonations}
            icon={<FiHeart />}
            iconColor="red.500"
          />
          <StatCard
            label="Total Donors"
            value={totalDonors}
            icon={<FiUsers />}
            iconColor="purple.500"
          />
          <StatCard
            label="Average Donation Amount"
            value={`$${formatCentsToDollars(averageDonationAmount)}`}
            icon={<FiTrendingUp />}
            iconColor="teal.500"
          />
          <StatCard
            label="Total Fees Saved"
            value={`$${formatCentsToDollars(totalFeesSaved)}`}
            icon={<FiPercent />}
            iconColor="orange.500"
          />
        </Flex>

        {/* Share hint when there are campaigns but no donations yet */}
        {campaigns.length > 0 &&
          totalDonations === 0 &&
          totalFundsRaised === 0 && (
            <Box
              bg="orange.50"
              border="1px solid"
              borderColor="orange.200"
              p={4}
              borderRadius="md"
              boxShadow="sm"
            >
              <Flex
                direction={{ base: "column", md: "row" }}
                align={{ base: "flex-start", md: "center" }}
                justify="space-between"
                gap={3}
              >
                <Text color="orange.800">
                  You’re almost there! Share your campaign to get your first
                  donation and see your charts come to life.
                </Text>
                <Flex gap={2}>
                  <Button
                    as={Link}
                    to="/campaigns"
                    colorScheme="orange"
                    variant="solid"
                    leftIcon={<FiShare2 />}
                  >
                    View Campaigns
                  </Button>
                  <Button as={Link} to="/campaigns/create">
                    Create New
                  </Button>
                </Flex>
              </Flex>
            </Box>
          )}

        {/* Filters */}
        <Flex direction={{ base: "column", md: "row" }} gap={6}>
          <Box flex="1">
            <Text fontWeight="bold" mb={2}>
              Filter by Campaign:
            </Text>
            <CheckboxGroup
              value={selectedCampaigns}
              onChange={(values) => setSelectedCampaigns(values)}
            >
              <Stack spacing={2}>
                {campaigns.map((campaign) => (
                  <Checkbox key={campaign.id} value={campaign.id.toString()}>
                    {campaign.name}
                  </Checkbox>
                ))}
              </Stack>
            </CheckboxGroup>
          </Box>
          <Box flex="1">
            <Text fontWeight="bold" mb={2}>
              Filter by Donation Type:
            </Text>
            <CheckboxGroup value={donationType} onChange={setDonationType}>
              <Stack spacing={2}>
                <Checkbox value="One-time">One-time</Checkbox>
                <Checkbox value="Monthly">Monthly</Checkbox>
              </Stack>
            </CheckboxGroup>
          </Box>
        </Flex>

        {/* Charts */}
        <Flex direction={{ base: "column", md: "row" }} gap={6} wrap="wrap">
          <Box flex="1" bg="gray.100" p={4} borderRadius="md" boxShadow="sm">
            <Heading size="md" mb={4}>
              Donations Over Time
            </Heading>
            {hasChartDatasetValues(donationsOverTimeData) &&
            hasLabels(donationsOverTimeData) ? (
              <Line data={donationsOverTimeData} />
            ) : (
              <EmptyChart
                message="No donations in the selected period."
                icon={<FiBarChart2 />}
              />
            )}
          </Box>
          <Box flex="1" bg="gray.100" p={4} borderRadius="md" boxShadow="sm">
            <Heading size="md" mb={4}>
              Donations by Campaign
            </Heading>
            {hasChartDatasetValues(donationsByCampaignData) &&
            hasLabels(donationsByCampaignData) ? (
              <Bar data={donationsByCampaignData} />
            ) : (
              <EmptyChart
                message="No donations by campaign to show yet."
                icon={<FiBarChart2 />}
              />
            )}
          </Box>
        </Flex>

        {/* Additional Insights */}
        <Flex direction={{ base: "column", md: "row" }} gap={6} wrap="wrap">
          <Box flex="1" bg="gray.100" p={4} borderRadius="md" boxShadow="sm">
            <Heading size="md" mb={4}>
              Donations by Type
            </Heading>
            {hasChartDatasetValues(donationsByTypeData) &&
            hasLabels(donationsByTypeData) ? (
              <Pie data={donationsByTypeData} />
            ) : (
              <EmptyChart
                message="No donation types to display yet."
                icon={<FiPieChart />}
              />
            )}
          </Box>
          <Box flex="1" bg="gray.100" p={4} borderRadius="md" boxShadow="sm">
            <Heading size="md" mb={4}>
              Donation Amount Distribution
            </Heading>
            {hasChartDatasetValues(donationAmountDistributionData) &&
            hasLabels(donationAmountDistributionData) ? (
              <Bar data={donationAmountDistributionData} />
            ) : (
              <EmptyChart
                message="No donation amounts to chart yet."
                icon={<FiBarChart2 />}
              />
            )}
          </Box>
        </Flex>

        {/* Recent Activity */}
        <Flex direction={{ base: "column", md: "row" }} gap={6} wrap="wrap">
          <RecentDonationsTable donations={recentDonations} />
          <RecentSubscriptionsTable subscriptions={recentSubscriptions} />
        </Flex>
      </Flex>
    </Box>
  );
}

// Helper components
const StatCard = ({ label, value, icon, iconColor }) => (
  <Box flex="1" bg="gray.100" p={4} borderRadius="md" boxShadow="sm">
    <Flex align="center" justify="space-between">
      <Stat>
        <StatLabel>{label}</StatLabel>
        <StatNumber>{value}</StatNumber>
      </Stat>
      {icon ? (
        <Box ml={4} color={iconColor || "gray.500"} fontSize="2xl">
          {icon}
        </Box>
      ) : null}
    </Flex>
  </Box>
);

const EmptyChart = ({ message, icon }) => (
  <Center height="220px" color="gray.500" flexDirection="column" gap={2}>
    <Box fontSize="24px">{icon}</Box>
    <Text fontSize="sm">{message}</Text>
  </Center>
);

const columnsRecentSubscriptionsTable = [
  {
    Header: "ID",
    accessor: "id",
  },
  {
    Header: "Amount",
    accessor: "amount",
    Cell: ({ value }) => `$${formatCentsToDollars(value)}`,
  },
  {
    Header: "Email",
    accessor: "email",
  },
  {
    Header: "Name",
    accessor: "first_name",
  },
  {
    Header: "Next Donation Date",
    accessor: "next_donation_date",
    Cell: ({ value }) => new Date(value).toLocaleDateString(),
  },
  {
    Header: "Frequency",
    accessor: "donation_type",
  },
  {
    Header: "Status",
    accessor: "status",
  },
];

const columnsRecentDonationsTable = [
  {
    Header: "Amount",
    accessor: "donation_amount",
    Cell: ({ value }) => `$${formatCentsToDollars(value)}`,
  },
  {
    Header: "Name",
    accessor: "first_name",
  },
  {
    Header: "Fees Saved",
    accessor: "fees_saved",
    Cell: ({ value }) => `$${formatCentsToDollars(value)}`,
  },
  {
    Header: "Date",
    accessor: "created_at",
    Cell: ({ value }) => new Date(value).toLocaleDateString(),
  },
  {
    Header: "Status",
    accessor: "status",
    Cell: ({ value }) => {
      const normalized = `${value || ""}`.toLowerCase();
      if (normalized === "succeeded" || normalized === "completed") {
        return <Badge colorScheme="green">Succeeded</Badge>;
      }
      if (normalized === "refund pending") {
        return <Badge colorScheme="blue">Refund Pending</Badge>;
      }
      if (normalized === "refunded") {
        return <Badge colorScheme="yellow">Refunded</Badge>;
      }
      if (normalized === "pending" || normalized === "pending_ach") {
        return <Badge colorScheme="blue">Pending</Badge>;
      }
      if (normalized === "failed" || normalized === "declined" || normalized === "canceled") {
        return <Badge colorScheme="red">Failed</Badge>;
      }
      return <Badge>{value || "Unknown"}</Badge>;
    },
  },
];

const RecentDonationsTable = ({ donations }) => (
  <Box flex="1" bg="gray.100" p={4} borderRadius="md" boxShadow="sm">
    <Heading size="md" mb={4}>
      Recent Donations
    </Heading>
    {donations && donations.length > 0 ? (
      <UniversalTable columns={columnsRecentDonationsTable} data={donations} />
    ) : (
      <Box textAlign="center" color="gray.600">
        <Text>No recent donations yet.</Text>
      </Box>
    )}
  </Box>
);

const RecentSubscriptionsTable = ({ subscriptions }) => (
  <Box flex="1" bg="gray.100" p={4} borderRadius="md" boxShadow="sm">
    <Heading size="md" mb={4}>
      Recent Subscriptions
    </Heading>
    {subscriptions && subscriptions.length > 0 ? (
      <UniversalTable
        columns={columnsRecentSubscriptionsTable}
        data={subscriptions}
      />
    ) : (
      <Box textAlign="center" color="gray.600">
        <Text>No recent subscriptions yet.</Text>
      </Box>
    )}
  </Box>
);

StatCard.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  icon: PropTypes.node,
  iconColor: PropTypes.string,
};

EmptyChart.propTypes = {
  message: PropTypes.string,
  icon: PropTypes.node,
};

RecentDonationsTable.propTypes = {
  donations: PropTypes.array,
};

RecentSubscriptionsTable.propTypes = {
  subscriptions: PropTypes.array,
};

export default Dashboard;
